import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { computeLangFromPath } from "@/lib/i18n";
import { SERVICES } from "@/data/services";
import { SERVICES_AR } from "@/data/services-ar";
import { SOLUTIONS } from "@/data/solutions";
import { SOLUTIONS_AR } from "@/data/solutions-ar";

const WA_NUMBER = (process.env.REACT_APP_WHATSAPP_NUMBER || "").replace(/\D/g, "");

const MESSAGES = {
  en: {
    general: "Hi Wafa Solutions, I came from wafa.om and I'd like to know more about your services.",
    service: (name) => `Hi Wafa Solutions, I came from wafa.om and I'm interested in your ${name} service.`,
  },
  ar: {
    general: "مرحباً وفا سوليوشنز، وصلت من موقع wafa.om وأرغب في معرفة المزيد عن خدماتكم.",
    service: (name) => `مرحباً وفا سوليوشنز، وصلت من موقع wafa.om وأرغب في معرفة المزيد عن خدمة ${name}.`,
  },
};

export const WA_LABEL = { en: "Chat on WhatsApp", ar: "تحدث معنا عبر واتساب" };
export const WA_SERVICE_LABEL = { en: "Ask about this service on WhatsApp", ar: "اسأل عن هذه الخدمة عبر واتساب" };

const AI_LAB_NAME = { en: "AI Lab", ar: "مختبر الذكاء الاصطناعي" };

// Short service name for the page; brand names (Shipploy, Leadaro) stay in English.
function serviceName(path, lang) {
  if (/^\/ai-lab\/?$/.test(path)) return AI_LAB_NAME[lang];
  const svc = path.match(/^\/services\/([^/]+)\/?$/);
  if (svc) return (lang === "ar" ? SERVICES_AR : SERVICES)[svc[1]]?.navLabel || null;
  const sol = path.match(/^\/solutions\/([^/]+)\/?$/);
  if (sol) return (lang === "ar" ? SOLUTIONS_AR : SOLUTIONS)[sol[1]]?.navLabel?.split(" — ")[0] || null;
  return null;
}

export function whatsAppMessage(pathname) {
  const { lang, enPath } = computeLangFromPath(pathname);
  const m = MESSAGES[lang];
  const name = serviceName(enPath, lang);
  return name ? m.service(name) : m.general;
}

export function useWhatsAppLink(source) {
  const { pathname } = useLocation();
  if (!WA_NUMBER) return null;
  const href = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(whatsAppMessage(pathname))}`;
  const onClick = () => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "whatsapp_click", {
        link_location: source,
        page_path: pathname,
        language: computeLangFromPath(pathname).lang,
      });
    }
  };
  return { href, onClick };
}

// Publishes how far an element's top edge sits above the viewport bottom as a CSS variable.
export function useViewportOffsetVar(ref, name, active = true) {
  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement.style;
    if (!active || !el) {
      root.removeProperty(name);
      return undefined;
    }
    const update = () => {
      const r = el.getBoundingClientRect();
      root.setProperty(name, r.height ? `${Math.max(0, window.innerHeight - r.top)}px` : "0px");
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      root.removeProperty(name);
    };
  }, [ref, name, active]);
}
